import {
  type ServiceDayBucket,
  type ServiceHealthState,
  type ServiceIncident,
  type ServiceStatusItem,
} from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';

import { cn } from '@/lib/cn';

type DayBucket = ServiceDayBucket;

export const STATE_LABEL: Record<ServiceHealthState, string> = {
  UP: 'Operational',
  DEGRADED: 'Degraded',
  DOWN: 'Down',
  UNKNOWN: 'Not checked',
};

export const STATE_TONE: Record<
  ServiceHealthState,
  'success' | 'warning' | 'destructive' | 'secondary'
> = {
  UP: 'success',
  DEGRADED: 'warning',
  DOWN: 'destructive',
  UNKNOWN: 'secondary',
};

const STATE_BG: Record<ServiceHealthState, string> = {
  UP: 'bg-emerald-500',
  DEGRADED: 'bg-amber-400',
  DOWN: 'bg-red-500',
  UNKNOWN: 'bg-slate-200',
};

export const StateDot = ({
  state,
  className,
}: {
  state: ServiceHealthState;
  className?: string;
}) => (
  <span
    aria-label={STATE_LABEL[state]}
    className={cn(
      'inline-block h-2.5 w-2.5 shrink-0 rounded-full',
      STATE_BG[state],
      state === 'DOWN' && 'animate-pulse',
      className,
    )}
  />
);

export const formatIst = (iso: string | null, withDate = true): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    ...(withDate ? { day: '2-digit', month: 'short' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds} sec`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm ? `${h} h ${rm} min` : `${h} h`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
};

export const formatSince = (iso: string | null): string => {
  if (!iso) return '';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return `for ${formatDuration(secs)}`;
};

/** 90 one-day cells, oldest left. Hover tells the day, worst state and downtime. */
export const UptimeBar = ({ days }: { days: DayBucket[] }) => (
  <div className="flex h-7 items-end gap-[2px]" role="img" aria-label="Last 90 days">
    {days.map((d) => (
      <div
        key={d.date}
        title={`${d.date}: ${STATE_LABEL[d.worst]}${
          d.downSeconds
            ? `, ${formatDuration(d.downSeconds)} affected in ${d.incidents} incident(s)`
            : ''
        }`}
        className={cn(
          'h-full flex-1 rounded-sm',
          STATE_BG[d.worst],
          d.worst === 'UNKNOWN' && 'opacity-60',
        )}
      />
    ))}
  </div>
);

export const UptimePct = ({ value, label }: { value: number | null; label: string }) => (
  <span className="text-muted-foreground text-xs">
    {label}{' '}
    <span
      className={cn(
        'font-medium',
        value === null
          ? ''
          : value >= 99.9
            ? 'text-emerald-700'
            : value >= 99
              ? 'text-amber-700'
              : 'text-red-700',
      )}
    >
      {value === null ? '-' : `${value.toFixed(2)}%`}
    </span>
  </span>
);

export const ServiceCard = ({
  item,
  selected,
  onSelect,
}: {
  item: ServiceStatusItem;
  selected: boolean;
  onSelect: (key: string) => void;
}) => {
  const failing = item.status === 'DOWN' || item.status === 'DEGRADED';
  return (
    <button
      type="button"
      onClick={() => onSelect(item.key)}
      className={cn(
        'bg-card hover:border-foreground/30 flex w-full flex-col gap-1.5 rounded-lg border p-3 text-left transition',
        selected && 'ring-primary ring-2',
        item.status === 'DOWN' && 'border-red-300 bg-red-50/60',
        item.status === 'DEGRADED' && 'border-amber-300 bg-amber-50/60',
      )}
    >
      <div className="flex items-center gap-2">
        <StateDot state={item.status} />
        <span className="truncate text-sm font-medium">{item.name}</span>
        <span className="text-muted-foreground ml-auto text-[11px]">
          {item.lastLatencyMs !== null ? `${item.lastLatencyMs} ms` : ''}
        </span>
      </div>
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <UzBadge tone={STATE_TONE[item.status]} pill>
          {STATE_LABEL[item.status]}
        </UzBadge>
        <span className="truncate">{formatSince(item.since)}</span>
      </div>
      {failing && item.lastError ? (
        <p className="line-clamp-2 text-xs text-red-700" title={item.lastError}>
          {item.lastError}
        </p>
      ) : item.detail ? (
        <p className="text-muted-foreground line-clamp-1 text-xs" title={item.detail}>
          {item.detail}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <UptimePct value={item.uptime24h} label="24h" />
        <UptimePct value={item.uptime30d} label="30d" />
        <span className="text-muted-foreground text-[11px]">
          checked {item.lastCheckedAt ? formatIst(item.lastCheckedAt, false) : 'never'}
        </span>
      </div>
    </button>
  );
};

export const IncidentRow = ({
  incident,
  onEditNote,
}: {
  incident: ServiceIncident;
  onEditNote: (incident: ServiceIncident) => void;
}) => (
  <tr className={cn('border-b text-sm', incident.endedAt === null && 'bg-red-50/50')}>
    <td className="px-3 py-2 align-top">
      <div className="flex items-center gap-2">
        <StateDot state={incident.endedAt === null ? incident.severity : 'UP'} />
        <span className="font-medium">{incident.serviceName}</span>
      </div>
      <div className="text-muted-foreground text-xs">
        <UzBadge tone={STATE_TONE[incident.severity]} pill>
          {incident.severity}
        </UzBadge>{' '}
        {incident.source === 'EXTERNAL'
          ? 'seen from outside'
          : incident.source === 'MANUAL'
            ? 'manual'
            : ''}
      </div>
    </td>
    <td className="px-3 py-2 align-top text-xs whitespace-nowrap">
      <div>{formatIst(incident.startedAt)}</div>
      <div className="text-muted-foreground">
        {incident.endedAt ? `to ${formatIst(incident.endedAt)}` : 'still open'}
      </div>
    </td>
    <td className="px-3 py-2 align-top text-xs font-medium whitespace-nowrap">
      {formatDuration(incident.durationSeconds)}
      {incident.endedAt === null ? ' +' : ''}
    </td>
    <td className="px-3 py-2 align-top text-xs">
      <div className="max-w-md break-words">{incident.cause}</div>
    </td>
    <td className="px-3 py-2 align-top text-xs">
      <button
        type="button"
        onClick={() => onEditNote(incident)}
        className="max-w-xs text-left break-words hover:underline"
        title="Edit note"
      >
        {incident.note ? incident.note : <span className="text-muted-foreground">Add note</span>}
      </button>
    </td>
  </tr>
);
